/**
 * Projection WGS84 → pixels d'affichage.
 *
 * Le fond de plan est une orthophoto géoréférencée en EPSG:4326 : les
 * coordonnées émises par les boîtiers sont donc directement projetables, sans
 * bibliothèque cartographique ni tuiles. C'est ce qui permet à cette interface
 * de n'avoir AUCUNE dépendance de carte — l'image et six coefficients suffisent.
 */

export interface Affine {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export interface BasemapMeta {
  code: string;
  label: string;
  is_fictional: boolean;
  image: { width_px: number; height_px: number };
  affine: Affine;
  extent: {
    lon_min: number;
    lon_max: number;
    lat_min: number;
    lat_max: number;
    center_lon: number;
    center_lat: number;
  };
  ground: { meters_per_pixel_x: number; meters_per_pixel_y: number };
  assets: { full: string; display: string; thumbnail: string };
}

export interface LonLat {
  lon: number;
  lat: number;
}

export interface Viewport {
  /** Échelle : pixels d'écran par pixel d'image source. */
  scale: number;
  /** Décalage en pixels d'écran. */
  offsetX: number;
  offsetY: number;
}

/** WGS84 → pixel image. Inverse exact de la transformation du GeoTIFF. */
export function lonLatToImage(affine: Affine, point: LonLat): { x: number; y: number } {
  const det = affine.a * affine.e - affine.b * affine.d;
  const dx = point.lon - affine.c;
  const dy = point.lat - affine.f;
  return {
    x: (affine.e * dx - affine.b * dy) / det,
    y: (affine.a * dy - affine.d * dx) / det,
  };
}

export function imageToLonLat(affine: Affine, x: number, y: number): LonLat {
  return {
    lon: affine.a * x + affine.b * y + affine.c,
    lat: affine.d * x + affine.e * y + affine.f,
  };
}

export function imageToScreen(viewport: Viewport, x: number, y: number): { x: number; y: number } {
  return { x: x * viewport.scale + viewport.offsetX, y: y * viewport.scale + viewport.offsetY };
}

export function lonLatToScreen(
  affine: Affine,
  viewport: Viewport,
  point: LonLat,
): { x: number; y: number } {
  const image = lonLatToImage(affine, point);
  return imageToScreen(viewport, image.x, image.y);
}

export function screenToLonLat(affine: Affine, viewport: Viewport, x: number, y: number): LonLat {
  return imageToLonLat(
    affine,
    (x - viewport.offsetX) / viewport.scale,
    (y - viewport.offsetY) / viewport.scale,
  );
}

/**
 * Cadre la vue sur une emprise géographique.
 *
 * Le facteur `padding` évite que les objets d'intérêt collent aux bords, ce qui
 * les rendrait illisibles au moment précis où on les regarde.
 */
export function fitBounds(
  affine: Affine,
  bounds: { lonMin: number; lonMax: number; latMin: number; latMax: number },
  canvas: { width: number; height: number },
  padding = 0.06,
): Viewport {
  const topLeft = lonLatToImage(affine, { lon: bounds.lonMin, lat: bounds.latMax });
  const bottomRight = lonLatToImage(affine, { lon: bounds.lonMax, lat: bounds.latMin });

  const width = Math.abs(bottomRight.x - topLeft.x);
  const height = Math.abs(bottomRight.y - topLeft.y);
  if (width === 0 || height === 0) return { scale: 1, offsetX: 0, offsetY: 0 };

  const scale = Math.min(
    (canvas.width * (1 - padding * 2)) / width,
    (canvas.height * (1 - padding * 2)) / height,
  );

  const centerImage = {
    x: (topLeft.x + bottomRight.x) / 2,
    y: (topLeft.y + bottomRight.y) / 2,
  };

  return {
    scale,
    offsetX: canvas.width / 2 - centerImage.x * scale,
    offsetY: canvas.height / 2 - centerImage.y * scale,
  };
}

/**
 * Matrice de dessin d'une surcouche, dans l'ordre attendu par `CanvasRenderingContext2D`.
 *
 * Une surcouche a son propre géoréférencement : ses pixels ne coïncident pas
 * avec ceux du fond de plan, et elle peut être tournée. On compose donc les
 * trois transformations — pixels surcouche → WGS84 → pixels fond de plan →
 * écran — en une seule matrice, que `transform()` applique avant `drawImage`.
 *
 * Le calcul passe par l'image de trois points seulement : l'origine et les deux
 * vecteurs unitaires. Les transformations étant affines, cela les détermine
 * entièrement, et l'on réutilise des fonctions déjà éprouvées plutôt que de
 * réécrire un produit matriciel à la main.
 */
export function surcoucheTransform(
  fond: Affine,
  surcouche: Affine,
  viewport: Viewport,
): [number, number, number, number, number, number] {
  const versFond = (x: number, y: number): { x: number; y: number } =>
    lonLatToImage(fond, imageToLonLat(surcouche, x, y));

  const origine = versFond(0, 0);
  const uniteColonne = versFond(1, 0);
  const uniteLigne = versFond(0, 1);

  return [
    (uniteColonne.x - origine.x) * viewport.scale,
    (uniteColonne.y - origine.y) * viewport.scale,
    (uniteLigne.x - origine.x) * viewport.scale,
    (uniteLigne.y - origine.y) * viewport.scale,
    origine.x * viewport.scale + viewport.offsetX,
    origine.y * viewport.scale + viewport.offsetY,
  ];
}

/** Échelle à afficher, en mètres par pixel d'écran. */
export function metersPerScreenPixel(meta: BasemapMeta, viewport: Viewport): number {
  return meta.ground.meters_per_pixel_x / viewport.scale;
}

/** Longueur d'une barre d'échelle « ronde » (1, 2, 5, 10, 20, 50 m…). */
export function niceScaleBar(
  metersPerPixel: number,
  targetPixels = 120,
): { meters: number; pixels: number } {
  const rawMeters = metersPerPixel * targetPixels;
  const magnitude = 10 ** Math.floor(Math.log10(rawMeters));
  const normalized = rawMeters / magnitude;
  const rounded = normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1;
  const meters = rounded * magnitude;
  return { meters, pixels: meters / metersPerPixel };
}
